import React, { useEffect, useState } from 'react';
import { FlaskConical, Loader2, RefreshCw, Database, Code } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAdminCompatibilityTest } from '@/hooks/admin/useAdminCompatibilityTest';
import { Progress } from '@/components/ui/progress';

const getRoleLabel = (role: string | null, gender: string | null): string => {
  if (role === 'sponsored') return 'Sugar Baby';
  if (role === 'sponsor') {
    if (gender === 'female') return 'Sugar Mommy';
    return 'Sugar Daddy';
  }
  return role || 'N/A';
};

export const CompatibilityTestBench: React.FC = () => {
  const {
    profiles,
    isLoadingProfiles,
    isCalculating,
    result,
    fetchProfiles,
    calculateCompatibility,
    clearResult,
  } = useAdminCompatibilityTest();

  const [user1Id, setUser1Id] = useState<string>('');
  const [user2Id, setUser2Id] = useState<string>('');
  const [skipCache, setSkipCache] = useState(false);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const handleCalculate = () => {
    if (user1Id && user2Id) {
      calculateCompatibility(user1Id, user2Id, { skipCache });
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-green-400';
    if (score >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 70) return 'bg-green-500';
    if (score >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-6">
      {/* Selection Area */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <FlaskConical className="h-5 w-5" />
            Configuração do Teste
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm text-white/60">Perfil 1 (quem está vendo)</label>
              <Select value={user1Id} onValueChange={setUser1Id}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Selecione o perfil..." />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-white/20">
                  {isLoadingProfiles ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  ) : (
                    profiles.map((profile) => (
                      <SelectItem
                        key={profile.id}
                        value={profile.id}
                        className="text-white focus:bg-white/10 focus:text-white"
                      >
                        <span className="flex items-center gap-2">
                          <span>{profile.username}</span>
                          <span className="text-xs text-white/40">
                            ({getRoleLabel(profile.role, profile.gender)})
                          </span>
                        </span>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-white/60">Perfil 2 (perfil alvo)</label>
              <Select value={user2Id} onValueChange={setUser2Id}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Selecione o perfil..." />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-white/20">
                  {isLoadingProfiles ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  ) : (
                    profiles
                      .filter((p) => p.id !== user1Id)
                      .map((profile) => (
                        <SelectItem
                          key={profile.id}
                          value={profile.id}
                          className="text-white focus:bg-white/10 focus:text-white"
                        >
                          <span className="flex items-center gap-2">
                            <span>{profile.username}</span>
                            <span className="text-xs text-white/40">
                              ({getRoleLabel(profile.role, profile.gender)})
                            </span>
                          </span>
                        </SelectItem>
                      ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="skipCache"
                checked={skipCache}
                onCheckedChange={(checked) => setSkipCache(checked === true)}
                className="border-white/40 data-[state=checked]:bg-luxury-pinkBright"
              />
              <label htmlFor="skipCache" className="text-sm text-white/80 cursor-pointer">
                Ignorar cache (forçar novo cálculo)
              </label>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleCalculate}
              disabled={!user1Id || !user2Id || isCalculating}
              className="bg-gradient-to-r from-[#C4406B] to-[#9F3764] hover:opacity-90"
            >
              {isCalculating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Calculando...
                </>
              ) : (
                <>
                  <FlaskConical className="h-4 w-4 mr-2" />
                  Calcular Compatibilidade
                </>
              )}
            </Button>

            {result && (
              <Button
                onClick={clearResult}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Limpar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results Area */}
      {result && (
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              <span className="flex items-center gap-2">
                Resultado
                {result.fromCache && (
                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full flex items-center gap-1">
                    <Database className="h-3 w-3" />
                    Cache
                  </span>
                )}
              </span>
              <span className={`text-3xl font-bold ${getScoreColor(result.score)}`}>
                {result.score}%
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="preview" className="w-full">
              <TabsList className="bg-white/5 border border-white/10">
                <TabsTrigger
                  value="preview"
                  className="data-[state=active]:bg-white/10 data-[state=active]:text-white"
                >
                  Preview
                </TabsTrigger>
                <TabsTrigger
                  value="raw"
                  className="data-[state=active]:bg-white/10 data-[state=active]:text-white"
                >
                  <Code className="h-4 w-4 mr-1" />
                  JSON Raw
                </TabsTrigger>
              </TabsList>

              <TabsContent value="preview" className="mt-4 space-y-4">
                {/* Score Bar */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/60">Compatibilidade</span>
                    <span className={getScoreColor(result.score)}>{result.score}%</span>
                  </div>
                  <Progress
                    value={result.score}
                    className="h-3 bg-white/10"
                  />
                </div>

                {/* AI Summary */}
                {result.aiSummary && (
                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="text-white">{result.aiSummary}</p>
                  </div>
                )}

                {/* Common Points */}
                {result.commonPoints.length > 0 && (
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                    <h4 className="text-green-400 text-sm font-medium mb-2">Pontos em comum</h4>
                    <ul className="space-y-1">
                      {result.commonPoints.map((point, i) => (
                        <li key={i} className="text-white text-sm flex items-start gap-2">
                          <span className="text-green-400 mt-0.5">✓</span>
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Differences */}
                {result.differences.length > 0 && (
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
                    <h4 className="text-orange-400 text-sm font-medium mb-2">Diferenças</h4>
                    <ul className="space-y-1">
                      {result.differences.map((diff, i) => (
                        <li key={i} className="text-white text-sm flex items-start gap-2">
                          <span className="text-orange-400 mt-0.5">⚠</span>
                          {diff}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="raw" className="mt-4">
                <div className="bg-black/30 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-xs text-gray-300 whitespace-pre-wrap break-words">
                    {JSON.stringify(result.rawResponse, null, 2)}
                  </pre>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
